import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Collapse,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { Link } from "react-router-dom";
import logo from "../img/logo_avg_vf1.png.jpg";
import { getMarqueeMessage } from "../models/MarqueeMessage";
import { useAuth } from "./AuthContext";
import LoginModal from "./LoginModal";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [marqueeData, setMarqueeData] = useState({
    message: "🌱 Agri Vision Gabon regroupe agriculteurs et éleveurs pour structurer et dynamiser les filières agricoles et d'élevage au Gabon. ✅ Réduire la dépendance alimentaire ✅ Renforcer l'autonomie des producteurs ✅ Assurer une production locale stable et compétitive",
    speed: 45
  });
  const [loading, setLoading] = useState(true);
  const { currentUser, userRole, isBuyer, logout, isAuthenticated } = useAuth();

  // Charger le message défilant depuis Firestore
  useEffect(() => {
    const fetchMarqueeMessage = async () => {
      try {
        const data = await getMarqueeMessage();
        setMarqueeData(data);
      } catch (err) {
        console.error('Erreur lors du chargement du message défilant:', err);
        // En cas d'erreur, on garde le message par défaut
      } finally {
        setLoading(false);
      }
    };
    
    fetchMarqueeMessage();
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
      // Rediriger vers la page d'accueil après la déconnexion
      window.location.href = '/';
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <>
      {/* Section supérieure avec texte défilant */}
      <Box
        sx={{
          backgroundColor: "#035417",
          color: "white",
          padding: "10px",
          overflow: "hidden",
          whiteSpace: "nowrap",
          position: "relative",
          marginBottom: "2px",
          display: "flex",
          alignItems: "center",
          height: "40px",
          width: "100%"
        }}
      >
        <Typography
          variant="body2"
          sx={{
            display: "inline-block",
            whiteSpace: "nowrap",
            position: "absolute",
            animation: `marquee-desktop ${marqueeData.speed}s linear infinite`,
            width: "max-content", // Assure que le texte ne soit pas coupé
            textAlign: "center",
            left: 0, // Assure que le texte commence à gauche
            right: 0, // Assure que le texte va jusqu'à droite
          }}
        >
          {marqueeData.message}
        </Typography>

        {/* Animation avec vitesse différente pour mobile et desktop */}
        <style>
          {`
            @keyframes marquee-desktop {
              from { transform: translateX(100vw); }
              to { transform: translateX(-100%); }
            }

            @media (max-width: 600px) {
              @keyframes marquee-desktop {
                from { transform: translateX(100vw); }
                to { transform: translateX(-100%); }
              }
              .MuiTypography-body2 {
                animation-duration: ${marqueeData.speed * 0.8}s !important; /* Version mobile ajustée */
                font-size: 0.8rem !important; /* Texte légèrement plus petit sur mobile */
              }
            }
          `}
        </style>
      </Box>

      {/* Navbar principale */}
      <AppBar
        position="sticky"
        style={{
          background: isAuthenticated 
            ? "linear-gradient(to right, #ffffff 0%, #FFF8DC 100%)" // Dégradé blanc vers doré pour les utilisateurs connectés
            : "#ffffff",
          color: "#002244",
          paddingBottom: 5,
          paddingTop: 5,
          transition: "background 0.3s ease",
        }}
      >
        <Toolbar style={{ justifyContent: "space-between" }}>
          {/* Logo */}
          <Box display="flex" alignItems="center" gap="10px">
            <img src={logo} alt="Logo" style={{ height: "60px" }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Agri Vision Gabon
            </Typography>
          </Box>

          {/* Menu Desktop */}
          <Box
            sx={{ display: { xs: "none", md: "flex" }, gap: "20px", alignItems: "center" }}
          >
            {/* Afficher les boutons de navigation seulement si l'utilisateur n'est pas un fermier */}
            {userRole !== 'farmer' && (
              <>
                <Button component={Link} to="/" color="inherit">
                  Accueil
                </Button>
                <Button
                  disabled
                  variant="contained"
                  startIcon={<StorefrontIcon sx={{ color: "#9ca3af !important" }} />}
                  sx={{
                    bgcolor: "#f3f4f6 !important",
                    color: "#9ca3af !important",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    textTransform: "none",
                    borderRadius: "20px",
                    px: 1.8,
                    py: 0.6,
                    boxShadow: "none !important",
                    border: "1px solid #e5e7eb",
                    cursor: "not-allowed !important",
                    pointerEvents: "auto !important",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.8
                  }}
                >
                  <span>Centrale d'Achat</span>
                  <Box
                    component="span"
                    sx={{
                      bgcolor: "#9ca3af",
                      color: "#ffffff",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      px: 0.9,
                      py: 0.2,
                      borderRadius: "10px",
                      textTransform: "none",
                      letterSpacing: "0.2px",
                      lineHeight: 1.2
                    }}
                  >
                    Bientôt disponible
                  </Box>
                </Button>
                <Button
                  color="inherit"
                  onClick={() => {
                    const avgSection = document.getElementById('avg-consulting');
                    if (avgSection) {
                      avgSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  AVG Consulting
                </Button>
                <Button component={Link} to="/news" color="inherit">
                  Actualités
                </Button>
                <Button
                  color="inherit"
                  onClick={() => {
                    const aboutSection = document.getElementById('about-avg');
                    if (aboutSection) {
                      aboutSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Qui sommes nous
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#014527",
                    color: "white",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#01331d" }
                  }}
                  onClick={() => {
                    const diagnosticSection = document.getElementById('diagnostic');
                    if (diagnosticSection) {
                      diagnosticSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Réserver un diagnostic
                </Button>
                {/* <Button component={Link} to="/sales" color="inherit">
                  Marché
                </Button> */}
              </>
            )}
            
            {/* Texte de salutation pour utilisateurs connectés - Desktop */}
            {isAuthenticated && currentUser && (
              <Typography
                variant="body2"
                sx={{
                  color: "#035417",
                  fontWeight: "500",
                  fontSize: "0.9rem",
                  fontStyle: "italic",
                  marginRight: "10px",
                  padding: "4px 8px",
                  backgroundColor: "rgba(3, 84, 23, 0.1)",
                  borderRadius: "12px",
                  border: "1px solid rgba(3, 84, 23, 0.2)"
                }}
              >
                {userRole === 'farmer' 
                  ? `Hi ${currentUser.email}` 
                  : `Hi ${currentUser.firstName || currentUser.prenom || currentUser.displayName?.split(' ')[0] || 'Utilisateur'}`
                }
              </Typography>
            )}
            
            {/* Boutons conditionnels selon l'état de connexion */}
            {userRole === 'farmer' ? (
              <>
                <IconButton
                  color="inherit"
                  onClick={handleUserMenuOpen}
                  sx={{ ml: 1 }}
                >
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={handleUserMenuClose} component={Link} to="/parametres">
                    Paramètres
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    Se déconnecter
                  </MenuItem>
                </Menu>
              </>
            ) : isBuyer ? (
              <>
                <IconButton
                  color="inherit"
                  onClick={handleUserMenuOpen}
                  sx={{ ml: 1 }}
                >
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={handleUserMenuClose} component={Link} to="/sales">
                    Tableau de bord
                  </MenuItem>
                  <MenuItem onClick={handleUserMenuClose} component={Link} to="/mes-commandes">
                    Mes commandes
                  </MenuItem>
                  <MenuItem onClick={handleUserMenuClose} component={Link} to="/parametres">
                    Paramètres
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    Se déconnecter
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button onClick={() => setLoginModalOpen(true)} color="inherit">
                S'identifier
              </Button>
            )}
          </Box>

          {/* Menu Mobile */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleMenu}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Toolbar>

        {/* Dropdown menu for mobile */}
        <Collapse in={menuOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              backgroundColor: "#ffffff",
              color: "#002244",
              padding: "10px 20px",
            }}
          >

            
            <Button
              component={Link}
              to="/"
              color="inherit"
              fullWidth
              onClick={toggleMenu}
            >
              Accueil
            </Button>
            <Button
              color="inherit"
              fullWidth
              onClick={() => {
                const avgSection = document.getElementById('avg-consulting');
                if (avgSection) {
                  avgSection.scrollIntoView({ behavior: 'smooth' });
                }
                toggleMenu();
              }}
            >
              AVG Consulting
            </Button>
            <Button
              component={Link}
              to="/news"
              color="inherit"
              fullWidth
              onClick={toggleMenu}
            >
              Actualités
            </Button>
            <Button
              color="inherit"
              fullWidth
              onClick={() => {
                const aboutSection = document.getElementById('about-avg');
                if (aboutSection) {
                  aboutSection.scrollIntoView({ behavior: 'smooth' });
                }
                toggleMenu();
              }}
            >
              Qui sommes nous
            </Button>
            <Button
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#014527",
                color: "white",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#01331d" }
              }}
              onClick={() => {
                const diagnosticSection = document.getElementById('diagnostic');
                if (diagnosticSection) {
                  diagnosticSection.scrollIntoView({ behavior: 'smooth' });
                }
                toggleMenu();
              }}
            >
              Réserver un diagnostic
            </Button>
            <Button
              disabled
              variant="contained"
              fullWidth
              startIcon={<StorefrontIcon sx={{ color: "#9ca3af !important" }} />}
              sx={{
                bgcolor: "#f3f4f6 !important",
                color: "#6b7280 !important",
                border: "1px solid #e5e7eb",
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "10px",
                my: 1,
                py: 1,
                px: 2,
                boxShadow: "none !important",
                cursor: "not-allowed !important",
                pointerEvents: "auto !important",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <span>Centrale d'Achat</span>
              <Box
                component="span"
                sx={{
                  bgcolor: "#9ca3af",
                  color: "#ffffff",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  px: 1,
                  py: 0.3,
                  borderRadius: "10px",
                  textTransform: "none"
                }}
              >
                Bientôt disponible
              </Box>
            </Button>
            
            {/* Boutons conditionnels selon l'état de connexion - Mobile */}
            {userRole === 'farmer' ? (
              <>
                <Button
                  component={Link}
                  to="/parametres"
                  color="inherit"
                  fullWidth
                  onClick={toggleMenu}
                >
                  Paramètres
                </Button>
                <Button
                  color="inherit"
                  fullWidth
                  onClick={() => {
                    handleLogout();
                    toggleMenu();
                  }}
                >
                  Se déconnecter
                </Button>
              </>
            ) : isBuyer ? (
              <>
                <Button
                  component={Link}
                  to="/sales"
                  color="inherit"
                  fullWidth
                  onClick={toggleMenu}
                >
                  Tableau de bord
                </Button>
                <Button
                  component={Link}
                  to="/mes-commandes"
                  color="inherit"
                  fullWidth
                  onClick={toggleMenu}
                >
                  Mes commandes
                </Button>
                <Button
                  component={Link}
                  to="/parametres"
                  color="inherit"
                  fullWidth
                  onClick={toggleMenu}
                >
                  Paramètres
                </Button>
                <Button
                  color="inherit"
                  fullWidth
                  onClick={() => {
                    handleLogout();
                    toggleMenu();
                  }}
                >
                  Se déconnecter
                </Button>
              </>
            ) : (
              <Button
                color="inherit"
                fullWidth
                onClick={() => {
                  setLoginModalOpen(true);
                  toggleMenu();
                }}
              >
                S'identifier
              </Button>
            )}
          </Box>
        </Collapse>
      </AppBar>
      
      {/* Modale de connexion */}
      <LoginModal 
        open={loginModalOpen} 
        onClose={() => setLoginModalOpen(false)} 
      />
    </>
  );
};

export default Navbar;
